import React, { useState, useEffect, useRef } from 'react';
import { Lead, LeadDetermination, DeterminationEntry } from '../../../types/lead.types';
import { updateLead } from '../../../services/lead.service';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { Clock, AlertTriangle } from 'lucide-react';
import { getBusinessDaysRemaining } from '../../../utils/dateUtils';

const determinationOptions = [
  { value: 'valid', label: 'Valid', color: 'bg-green-100 text-green-800' },
  { value: 'invalid', label: 'Invalid', color: 'bg-red-100 text-red-800' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' }
];

interface LeadDeterminationTabProps {
  lead: Lead;
  editedLead: Lead;
  setEditedLead: React.Dispatch<React.SetStateAction<Lead>>;
  onUpdate: (lead: Lead) => void;
  saving: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  validationDeadlineDays?: number;
}

const LeadDeterminationTab: React.FC<LeadDeterminationTabProps> = ({
  lead,
  editedLead,
  setEditedLead,
  onUpdate,
  saving,
  setSaving,
  validationDeadlineDays
}) => {
  const { currentUser, userProfile } = useAuth();
  const [determinationNotes, setDeterminationNotes] = useState('');

  const currentDeterminationOption = determinationOptions.find(opt => opt.value === editedLead.determination);

  const handleDeterminationChange = async (newDetermination: LeadDetermination, notes?: string) => {
    if (!currentUser) return;

    // Create new determination entry - only include notes if it has a value
    const entryNotes = notes || determinationNotes || '';
    const newEntry: DeterminationEntry = {
      determination: newDetermination,
      timestamp: Timestamp.now(),
      userId: currentUser.uid,
      userName: userProfile?.name || 'Unknown User'
    };
    // Only add notes field if there are actual notes
    if (entryNotes.trim()) {
      newEntry.notes = entryNotes.trim();
    }

    // Add to history
    const existingHistory = editedLead.determinationHistory || [];
    const updatedHistory = [...existingHistory, newEntry];

    const updatedDeterminationNotes = entryNotes || editedLead.determinationNotes || '';

    const updatedLead = {
      ...editedLead,
      determination: newDetermination,
      determinationHistory: updatedHistory,
      determinationNotes: updatedDeterminationNotes
    };
    setEditedLead(updatedLead);

    // Auto-save determination changes - only include fields with values
    setSaving(true);
    try {
      const updateData: Record<string, any> = {
        determination: newDetermination,
        determinationHistory: updatedHistory,
        lastUpdated: Timestamp.now()
      };
      // Only include determinationNotes if it has a value
      if (updatedDeterminationNotes) {
        updateData.determinationNotes = updatedDeterminationNotes;
      }

      // Pass user context so the determination change is recorded in
      // determinationChanges (drives billing carry-over adjustments).
      await updateLead(lead.id, updateData, currentUser.uid, userProfile?.name || 'Unknown User');
      toast.success('Determination updated');
      setDeterminationNotes('');
      onUpdate(updatedLead);
    } catch (error) {
      console.error('Error updating determination:', error);
      toast.error('Failed to update determination');
    } finally {
      setSaving(false);
    }
  };

  // Calculate deadline info
  const deadlineInfo = validationDeadlineDays && editedLead.determination === 'pending' && editedLead.createdAt
    ? getBusinessDaysRemaining(editedLead.createdAt.toDate(), validationDeadlineDays)
    : null;

  // Auto-validate overdue leads on view
  const autoValidatedRef = useRef(false);
  useEffect(() => {
    if (
      deadlineInfo?.isOverdue &&
      editedLead.determination === 'pending' &&
      !autoValidatedRef.current &&
      !saving
    ) {
      autoValidatedRef.current = true;

      const autoValidate = async () => {
        const autoNote = 'Auto-validated: Contact deadline exceeded';
        const newEntry: DeterminationEntry = {
          determination: 'valid',
          timestamp: Timestamp.now(),
          userId: 'system',
          userName: 'System',
          notes: autoNote
        };

        const existingHistory = editedLead.determinationHistory || [];
        const updatedHistory = [...existingHistory, newEntry];

        const updatedLead = {
          ...editedLead,
          determination: 'valid' as LeadDetermination,
          status: 'enrolled' as any,
          determinationHistory: updatedHistory,
          determinationNotes: autoNote
        };

        setSaving(true);
        try {
          await updateLead(lead.id, {
            determination: 'valid',
            status: 'enrolled',
            determinationHistory: updatedHistory,
            determinationNotes: autoNote,
            lastUpdated: Timestamp.now()
          }, currentUser?.uid, userProfile?.name || 'Unknown User');
          setEditedLead(updatedLead);
          onUpdate(updatedLead);
          toast.success('Lead auto-validated — contact deadline exceeded');
        } catch (error) {
          console.error('Error auto-validating lead:', error);
          autoValidatedRef.current = false; // Allow retry
        } finally {
          setSaving(false);
        }
      };

      autoValidate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadlineInfo?.isOverdue, editedLead.determination, lead.id]);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Lead Determination</h3>

      {/* Contact Deadline */}
      {deadlineInfo && (
        <div className={`border rounded-lg p-4 ${
          deadlineInfo.isOverdue
            ? 'bg-red-50 border-red-200'
            : deadlineInfo.daysRemaining <= 2
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {deadlineInfo.isOverdue ? (
              <AlertTriangle className="w-5 h-5 text-red-600" />
            ) : (
              <Clock className="w-5 h-5 text-gray-600" />
            )}
            <h4 className={`font-medium ${
              deadlineInfo.isOverdue ? 'text-red-800' : deadlineInfo.daysRemaining <= 2 ? 'text-yellow-800' : 'text-green-800'
            }`}>
              Contact Deadline
            </h4>
          </div>
          <div className="ml-7">
            {deadlineInfo.isOverdue ? (
              <p className="text-sm text-red-700 font-medium">
                Overdue — Lead will be auto-validated for billing
              </p>
            ) : (
              <p className={`text-sm font-medium ${deadlineInfo.daysRemaining <= 2 ? 'text-yellow-700' : 'text-green-700'}`}>
                {deadlineInfo.daysRemaining} business day{deadlineInfo.daysRemaining !== 1 ? 's' : ''} remaining
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Due by {format(deadlineInfo.deadlineDate, 'EEEE, MMM d, yyyy')} ({validationDeadlineDays} business day{validationDeadlineDays !== 1 ? 's' : ''} from submission)
            </p>
          </div>
        </div>
      )}

      {/* Current Status */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-gray-700">Current Determination</label>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${currentDeterminationOption?.color || 'bg-gray-100 text-gray-800'}`}>
            {currentDeterminationOption?.label || 'Pending'}
          </span>
        </div>

        {/* Field users cannot edit determination */}
        {userProfile?.role === 'field' ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-gray-600 text-sm">
              Only administrators and client users can update lead determination.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Update Determination</label>
              <div className="flex gap-2">
                {determinationOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      if (option.value !== editedLead.determination) {
                        handleDeterminationChange(option.value as LeadDetermination);
                      }
                    }}
                    disabled={saving}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      editedLead.determination === option.value
                        ? option.color + ' ring-2 ring-offset-2 ring-indigo-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes for next determination change</label>
              <textarea
                value={determinationNotes}
                onChange={(e) => setDeterminationNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Add notes explaining this determination (optional)..."
              />
            </div>
          </div>
        )}
      </div>

      {/* Determination History */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Determination History
        </h4>

        <div className="space-y-3">
          {editedLead.determinationHistory && editedLead.determinationHistory.length > 0 ? (
            [...editedLead.determinationHistory]
              .sort((a, b) => {
                const dateA = a.timestamp?.toDate?.() || new Date(0);
                const dateB = b.timestamp?.toDate?.() || new Date(0);
                return dateB.getTime() - dateA.getTime();
              })
              .map((entry, index) => {
                const option = determinationOptions.find(o => o.value === entry.determination);
                return (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${option?.color || 'bg-gray-100 text-gray-800'}`}>
                          {option?.label || entry.determination}
                        </span>
                        <span className="text-sm text-gray-500">
                          by {entry.userName || 'Unknown'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {entry.timestamp?.toDate ? format(entry.timestamp.toDate(), 'MMM d, yyyy h:mm a') : 'Unknown'}
                      </span>
                    </div>
                    {entry.notes && (
                      <p className="text-sm text-gray-600 mt-2 pl-2 border-l-2 border-gray-200">
                        {entry.notes}
                      </p>
                    )}
                  </div>
                );
              })
          ) : (
            <div className="text-center py-6 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-sm">No determination changes recorded yet.</p>
              <p className="text-gray-400 text-xs mt-1">Changes will be tracked when you update the determination above.</p>
            </div>
          )}
        </div>
      </div>

      {/* Info about billing/reporting */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Determination changes are tracked for billing and reporting purposes.
          Valid leads are counted toward project goals and may affect field staff compensation.
        </p>
      </div>
    </div>
  );
};

export default LeadDeterminationTab;
